import { AlignType, VerticalAlignType, DreamBlock } from '@typedream/core';
import { BLOCK_ELEMENT_KEY } from './nodes/collection/components/CollectionDataElement/types';
import {
  ELEMENT_BLOCKQUOTE,
  ELEMENT_BUTTON,
  ELEMENT_CODE_BLOCK,
  ELEMENT_CODE_LINE,
  ELEMENT_CONTAINER,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_IMAGE,
  ELEMENT_LI,
  ELEMENT_LINK,
  ELEMENT_MEDIA_EMBED,
  ELEMENT_OL,
  ELEMENT_PARAGRAPH,
  ELEMENT_RICH_TEXT_CONTENT,
  ELEMENT_TODO_LI,
  ELEMENT_UL,
  KEY_SYNCED_CONTAINER,
} from './pluginKeys';

export const DEFAULT_MAX_WIDTH = 700;
export const DEFAULT_NAVBAR_HEIGHT = 60;
export const DEFAULT_ALIGN_TYPE: AlignType = 'align_left';
export const DEFAULT_VERTICAL_ALIGN_TYPE: VerticalAlignType = 'align_center';
export const ELEMENT_DEFAULT_ALIGN_TYPE: {
  [key in DreamBlock['type']]?: AlignType;
} = {
  column: 'column_dynamic',
  img: 'align_center',
};

export const TOOLS_INTEGRATIONS_DOCUMENTATION_LINK = 'https://typedream.com/docs/integration';
export const ASSET_TYPEDREAM_LINK = 'https://www.typedream.com/assets';
export const GOOGLE_FONTS_API_KEY = 'AIzaSyAftmANGKLN63qzQ5_ygmqEge_gVH8N-yc';

export enum NAVBAR_ALIGNMENT {
  START = 'start',
  CENTER = 'center',
  END = 'end',
}

export enum NAVBAR_POSITION {
  FIXED = 'fixed',
  RELATIVE = 'relative',
}

export enum NAVBAR_STYLE {
  PLAIN = 'plain',
  SHADOW = 'shadow',
  BORDER = 'border',
}

export enum NAVBAR_ITEM_TYPE {
  LINK = 'link',
  BUTTON = 'button',
  // eventually we want to use types from packages/core
  // https://github.com/typedreamhq/frontend/blob/92c4e73c8ef82ac04b695ea166005d7d8034fe87/packages/core/src/types/navbar.ts#L69
  DROPDOWN = 'dropdown',
  WEB3 = 'web3_button',
}

export enum NAVBAR_DRAGGABLE_TYPE {
  ITEM_END = 'navbar_item_end',
  ITEM_MID = 'navbar_item_mid',
}
export const DARK_THEME = 'dark';
export const MOBILE_CUTOFF = 700;
export const COLLECTION_CONNECT_ITEM_PAGE_URL = 'ITEM_PAGE_URL';
export const DEFAULT_COLLECTION_DATA_ID = 'id';
export const LOGO_NAME_ABBREVIATED = 'TD';
export const COLLECTION_PUBLISHED_CONTENT_COLUMN_NAME = 'TD:page_content_published';
export const COLLECTION_CONTENT_COLUMN_NAME = 'TD:page_content';
export const ITEM_PAGE_DEFAULT_FIELDS = {
  METADATA: {
    fieldName: `${LOGO_NAME_ABBREVIATED}:metadata`,
    label: 'Metadata',
    description: 'For SEO Settings, Twitter cards, etc.',
  },
  SLUG: {
    fieldName: `${LOGO_NAME_ABBREVIATED}:slug`,
    label: 'Slug',
    description: 'To specify the URL for each item',
  },
  PUBLISHED_CONTENT: {
    fieldName: COLLECTION_PUBLISHED_CONTENT_COLUMN_NAME,
    label: 'Page Content',
    description: "To store your page's published rich text content",
  },
  CONTENT: {
    fieldName: COLLECTION_CONTENT_COLUMN_NAME,
    label: 'Page Content Draft',
    description: "To store your page's draft rich text content",
  },
};

// Collection Templates
export const DEFAULT_BIG_SIDEBAR_WIDTH = 400;
export const SIDEBAR_ZINDEX_OPEN = 1000;

export enum LEVEL {
  LEAF = 'leaf',
  BLOCK = 'block',
}

export interface IAllowedConnectedTypes {
  elementKey: BLOCK_ELEMENT_KEY;
  level: LEVEL;
  defaultSelected: boolean;
  optionDisabled: boolean;
  defaultColumn?: string;
}
export const ALLOWED_CONNECT_TYPES = {
  [ELEMENT_PARAGRAPH]: {
    [BLOCK_ELEMENT_KEY.TEXT]: {
      elementKey: BLOCK_ELEMENT_KEY.TEXT,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
    [BLOCK_ELEMENT_KEY.URL]: {
      elementKey: BLOCK_ELEMENT_KEY.URL,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [ELEMENT_H1]: {
    [BLOCK_ELEMENT_KEY.TEXT]: {
      elementKey: BLOCK_ELEMENT_KEY.TEXT,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
    [BLOCK_ELEMENT_KEY.URL]: {
      elementKey: BLOCK_ELEMENT_KEY.URL,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [ELEMENT_H2]: {
    [BLOCK_ELEMENT_KEY.TEXT]: {
      elementKey: BLOCK_ELEMENT_KEY.TEXT,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
    [BLOCK_ELEMENT_KEY.URL]: {
      elementKey: BLOCK_ELEMENT_KEY.URL,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [ELEMENT_H3]: {
    [BLOCK_ELEMENT_KEY.TEXT]: {
      elementKey: BLOCK_ELEMENT_KEY.TEXT,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
    [BLOCK_ELEMENT_KEY.URL]: {
      elementKey: BLOCK_ELEMENT_KEY.URL,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [ELEMENT_LI]: {
    [BLOCK_ELEMENT_KEY.TEXT]: {
      elementKey: BLOCK_ELEMENT_KEY.TEXT,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [ELEMENT_CODE_BLOCK]: {
    [BLOCK_ELEMENT_KEY.TEXT]: {
      elementKey: BLOCK_ELEMENT_KEY.TEXT,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [ELEMENT_CODE_LINE]: {
    [BLOCK_ELEMENT_KEY.TEXT]: {
      elementKey: BLOCK_ELEMENT_KEY.TEXT,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [ELEMENT_BLOCKQUOTE]: {
    [BLOCK_ELEMENT_KEY.TEXT]: {
      elementKey: BLOCK_ELEMENT_KEY.TEXT,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [ELEMENT_IMAGE]: {
    [BLOCK_ELEMENT_KEY.HREF]: {
      elementKey: BLOCK_ELEMENT_KEY.HREF,
      level: LEVEL.BLOCK,
      defaultSelected: false,
      optionDisabled: false,
    },
    [BLOCK_ELEMENT_KEY.URL]: {
      elementKey: BLOCK_ELEMENT_KEY.URL,
      level: LEVEL.BLOCK,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [ELEMENT_BUTTON]: {
    [BLOCK_ELEMENT_KEY.TEXT]: {
      elementKey: BLOCK_ELEMENT_KEY.TEXT,
      level: LEVEL.LEAF,
      defaultSelected: false,
      optionDisabled: false,
    },
    [BLOCK_ELEMENT_KEY.URL]: {
      elementKey: BLOCK_ELEMENT_KEY.URL,
      level: LEVEL.BLOCK,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [ELEMENT_MEDIA_EMBED]: {
    [BLOCK_ELEMENT_KEY.URL]: {
      elementKey: BLOCK_ELEMENT_KEY.URL,
      level: LEVEL.BLOCK,
      defaultSelected: false,
      optionDisabled: false,
    },
    [BLOCK_ELEMENT_KEY.HTML]: {
      elementKey: BLOCK_ELEMENT_KEY.HTML,
      level: LEVEL.BLOCK,
      defaultSelected: false,
      optionDisabled: false,
    },
    [BLOCK_ELEMENT_KEY.FILE]: {
      elementKey: BLOCK_ELEMENT_KEY.FILE,
      level: LEVEL.BLOCK,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [ELEMENT_CONTAINER]: {
    [BLOCK_ELEMENT_KEY.BACKGROUND_IMAGE]: {
      elementKey: BLOCK_ELEMENT_KEY.BACKGROUND_IMAGE,
      level: LEVEL.BLOCK,
      defaultSelected: false,
      optionDisabled: false,
    },
    [BLOCK_ELEMENT_KEY.URL]: {
      elementKey: BLOCK_ELEMENT_KEY.URL,
      level: LEVEL.BLOCK,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [KEY_SYNCED_CONTAINER]: {
    [BLOCK_ELEMENT_KEY.BACKGROUND_IMAGE]: {
      elementKey: BLOCK_ELEMENT_KEY.BACKGROUND_IMAGE,
      level: LEVEL.BLOCK,
      defaultSelected: false,
      optionDisabled: false,
    },
    [BLOCK_ELEMENT_KEY.URL]: {
      elementKey: BLOCK_ELEMENT_KEY.URL,
      level: LEVEL.BLOCK,
      defaultSelected: false,
      optionDisabled: false,
    },
  },
  [ELEMENT_RICH_TEXT_CONTENT]: {
    content: {
      elementKey: 'content',
      level: LEVEL.BLOCK,
      defaultSelected: true,
      optionDisabled: true,
      defaultColumn: COLLECTION_CONTENT_COLUMN_NAME,
    },
  },
};

export const CONNECTED_TYPES_OPTIONS_TEXTS = {
  [BLOCK_ELEMENT_KEY.TEXT]: {
    title: 'text',
    autofill: 'Title',
  },
  [BLOCK_ELEMENT_KEY.URL]: {
    title: 'url',
    autofill: 'Link',
  },
  [BLOCK_ELEMENT_KEY.HTML]: {
    title: 'html',
    autofill: 'HTML',
  },
  [BLOCK_ELEMENT_KEY.FILE]: {
    title: 'file url',
    autofill: 'File Link',
  },
  [BLOCK_ELEMENT_KEY.BACKGROUND_IMAGE]: {
    title: 'background image',
    autofill: 'Background Image',
  },
  content: {
    title: 'content',
    autofill: 'Content',
  },
};

export const CONNECTED_TYPES_OPTIONS_IMAGE = {
  [BLOCK_ELEMENT_KEY.HREF]: {
    title: 'url',
    autofill: 'Link',
  },
  [BLOCK_ELEMENT_KEY.URL]: {
    title: 'image',
    autofill: 'Image',
  },
};

export const SPECIAL_SCHEMA_COLUMN_NAME = {
  [COLLECTION_CONNECT_ITEM_PAGE_URL]: "Link to this item's page",
};

export const TEXT_TYPES = [
  ELEMENT_PARAGRAPH,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_OL,
  ELEMENT_UL,
  ELEMENT_LI,
  ELEMENT_TODO_LI,
  ELEMENT_CODE_BLOCK,
  ELEMENT_BLOCKQUOTE,
  ELEMENT_LINK,
  ELEMENT_BUTTON,
];

export const LINK_TYPES = [ELEMENT_LINK, ELEMENT_BUTTON, ELEMENT_CONTAINER, ELEMENT_IMAGE];
